import type { GalleryImagePickerItem } from '@wapharma/design-system';
import { unwrapData } from '@wapharma/shared';
import { resolveApiMediaUrl } from './resolveApiMediaUrl';

export type CatalogMediaLibraryItem = {
  id: string;
  key: string;
  url: string;
  fileName: string | null;
  createdAt?: string;
};

function withResolvedMediaUrl(item: CatalogMediaLibraryItem): CatalogMediaLibraryItem {
  return { ...item, url: resolveApiMediaUrl(item.url) ?? item.url };
}

/** Normalize list/upload responses (`{ items }`, `{ data: { items } }`, or bare array). */
export function parseCatalogMediaLibraryList(raw: unknown): CatalogMediaLibraryItem[] {
  const body = unwrapData<unknown>(raw);
  if (Array.isArray(body)) {
    return body
      .filter((row): row is CatalogMediaLibraryItem => Boolean(row && typeof row === 'object' && 'key' in row))
      .map(withResolvedMediaUrl);
  }
  if (body && typeof body === 'object' && Array.isArray((body as { items?: unknown }).items)) {
    return (body as { items: CatalogMediaLibraryItem[] }).items.map(withResolvedMediaUrl);
  }
  return [];
}

export function parseCatalogMediaLibraryItem(raw: unknown): CatalogMediaLibraryItem {
  const body = unwrapData<unknown>(raw);
  if (body && typeof body === 'object' && 'key' in body) {
    return withResolvedMediaUrl(body as CatalogMediaLibraryItem);
  }
  throw new Error('Réponse bibliothèque média invalide.');
}

export function libraryItemsToPickerItems(items: CatalogMediaLibraryItem[]): GalleryImagePickerItem[] {
  return items.map((item) => ({
    id: item.key,
    url: resolveApiMediaUrl(item.url) ?? item.url,
    label: item.fileName ?? undefined,
  }));
}

export function resolveCatalogMediaPickerUrl(
  key: string,
  libraryItems: CatalogMediaLibraryItem[],
  slideUrl?: string | null,
): string | null {
  if (slideUrl) return resolveApiMediaUrl(slideUrl) ?? slideUrl;
  const trimmed = key.trim();
  if (!trimmed) return null;
  const hit = libraryItems.find((i) => i.key === trimmed)?.url ?? null;
  return resolveApiMediaUrl(hit) ?? hit;
}

/** Dedupe by picker id (storage key). First group wins (library before product-only extras). */
export function mergeCatalogGalleryPickerItems(
  ...groups: GalleryImagePickerItem[][]
): GalleryImagePickerItem[] {
  const seen = new Set<string>();
  const merged: GalleryImagePickerItem[] = [];
  for (const group of groups) {
    for (const item of group) {
      if (seen.has(item.id)) continue;
      seen.add(item.id);
      merged.push({ ...item, url: resolveApiMediaUrl(item.url) ?? item.url });
    }
  }
  return merged;
}
